import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { bodyMassIndex, dataService } from '../../services/dataService';
import { ClinicalRecord } from '../../types/domain';
import LazyTrendChart from '../components/charts/LazyTrendChart';
import NextVisitCard from '../components/NextVisitCard';

const todayLocal = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
};

/** Quarter label for a date, e.g. "2026 Jan–Mar". */
const quarterOf = (date: string): string => {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) {
    return 'Tidak diketahui';
  }
  const labels = ['Jan–Mar', 'Apr–Jun', 'Jul–Sep', 'Okt–Des'];
  return `${d.getFullYear()} ${labels[Math.floor(d.getMonth() / 3)]}`;
};

const numOrNull = (v: string): number | null => {
  const t = v.trim();
  if (!t) {
    return null;
  }
  const n = Number(t.replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};

export default function ClinicalDataPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [recordedAt, setRecordedAt] = useState(todayLocal());
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [hba1c, setHba1c] = useState('');

  const load = useCallback(async () => {
    if (!user) {
      return;
    }
    setLoading(true);
    try {
      setRecords(await dataService.getClinicalRecords(user.id));
    } catch (err) {
      console.error(err);
      setError('Gagal memuat data klinis.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Oldest-first for the chart — time runs left to right — while the list
   * below stays newest-first, which is what someone checking a recent result
   * wants.
   */
  const hba1cSeries = useMemo(
    () =>
      records
        .filter(r => r.hba1c !== null)
        .slice()
        .reverse()
        .map(r => ({ date: r.recordedAt, hba1c: r.hba1c })),
    [records]
  );

  /** SoW: "Riwayat HbA1c tersimpan setiap 3 bulan" — browsable per quarter. */
  const byQuarter = useMemo(() => {
    const groups = new Map<string, ClinicalRecord[]>();
    for (const record of records) {
      const key = quarterOf(record.recordedAt);
      groups.set(key, [...(groups.get(key) ?? []), record]);
    }
    return [...groups.entries()];
  }, [records]);

  const latest = records[0];

  const handleSave = async () => {
    if (!user) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await dataService.saveClinicalRecord(user.id, {
        recordedAt,
        weightKg: numOrNull(weight),
        heightCm: numOrNull(height),
        hba1c: numOrNull(hba1c)
      });
      setWeight('');
      setHeight('');
      setHba1c('');
      setNotice('Data klinis tersimpan.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan data klinis.');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (record: ClinicalRecord) => {
    if (!user) {
      return;
    }
    if (!window.confirm(`Hapus data tanggal ${record.recordedAt}?`)) {
      return;
    }
    setBusy(true);
    try {
      await dataService.deleteClinicalRecord(user.id, record.id);
      await load();
    } catch (err: any) {
      setError(err?.message || 'Gagal menghapus data.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Menyiapkan data klinis...</div>
      </div>
    );
  }

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div className="screen-card" role="group" aria-labelledby="clinical-title">
        <div id="clinical-title" className="section-title">
          Data klinis
        </div>
        <div className="subtitle">
          Catat berat badan, tinggi badan dan HbA1c setiap kali kontrol.
        </div>

        {latest ? (
          <div className="clinical-latest">
            <div className="clinical-stat">
              <span className="clinical-stat-label">HbA1c terakhir</span>
              <span className="clinical-stat-value">
                {latest.hba1c ?? '–'}
                {latest.hba1c !== null ? <span className="clinical-unit"> %</span> : null}
              </span>
              <span className="clinical-stat-note">{latest.recordedAt}</span>
            </div>
            <div className="clinical-stat">
              <span className="clinical-stat-label">Berat / tinggi</span>
              <span className="clinical-stat-value">
                {latest.weightKg ?? '–'}
                <span className="clinical-unit"> kg</span> · {latest.heightCm ?? '–'}
                <span className="clinical-unit"> cm</span>
              </span>
              <span className="clinical-stat-note">
                {bodyMassIndex(latest) !== null
                  ? `IMT ${bodyMassIndex(latest)}`
                  : 'IMT belum bisa dihitung'}
              </span>
            </div>
          </div>
        ) : null}

        {hba1cSeries.length > 1 ? (
          <div className="clinical-chart">
            <LazyTrendChart
              title="Perubahan HbA1c"
              subtitle="Persen (%)"
              data={hba1cSeries}
              categoryKey="date"
              categoryLabel="Tanggal"
              series={[{ key: 'hba1c', label: 'HbA1c' }]}
              unit="%"
              yDomain={[0, 'auto']}
            />
          </div>
        ) : hba1cSeries.length === 1 ? (
          <div className="subtitle" style={{ marginTop: 12 }}>
            Grafik muncul setelah ada minimal dua hasil HbA1c.
          </div>
        ) : null}

        <div className="clinical-form">
          <div className="subtitle">Tambah / ubah data</div>
          <div className="field-group">
            <div className="field">
              <label htmlFor="clinical-date">Tanggal pemeriksaan</label>
              <input
                id="clinical-date"
                type="date"
                value={recordedAt}
                max={todayLocal()}
                onChange={e => setRecordedAt(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="clinical-hba1c">HbA1c (%)</label>
              <input
                id="clinical-hba1c"
                type="number"
                inputMode="decimal"
                step={0.1}
                min={2}
                max={20}
                value={hba1c}
                onChange={e => setHba1c(e.target.value)}
              />
            </div>
          </div>
          <div className="field-group">
            <div className="field">
              <label htmlFor="clinical-weight">Berat badan (kg)</label>
              <input
                id="clinical-weight"
                type="number"
                inputMode="decimal"
                step={0.1}
                min={0}
                max={250}
                value={weight}
                onChange={e => setWeight(e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="clinical-height">Tinggi badan (cm)</label>
              <input
                id="clinical-height"
                type="number"
                inputMode="decimal"
                step={0.5}
                min={30}
                max={250}
                value={height}
                onChange={e => setHeight(e.target.value)}
              />
            </div>
          </div>

          <div className="subtitle" style={{ fontSize: 11 }}>
            Isi minimal satu. Tanggal yang sama akan memperbarui data sebelumnya.
          </div>

          {error ? <div className="target-band-error">{error}</div> : null}
          {notice ? <div className="target-band-preview">{notice}</div> : null}

          <div className="target-actions">
            <button type="button" className="btn-primary" disabled={busy} onClick={handleSave}>
              {busy ? 'Menyimpan...' : 'Simpan data klinis'}
            </button>
          </div>
        </div>

        {records.length === 0 ? (
          <div className="subtitle" style={{ marginTop: 14 }}>
            Belum ada data klinis tersimpan.
          </div>
        ) : (
          <div className="clinical-history">
            <div className="subtitle">Riwayat per 3 bulan</div>
            {byQuarter.map(([quarter, group]) => (
              <div key={quarter} className="clinical-quarter">
                <div className="clinical-quarter-label">{quarter}</div>
                <ul className="clinical-list">
                  {group.map(record => (
                    <li key={record.id} className="clinical-item">
                      <div className="clinical-item-main">
                        <span className="clinical-item-date">{record.recordedAt}</span>
                        <span className="clinical-item-values">
                          {record.hba1c !== null ? `HbA1c ${record.hba1c}%` : null}
                          {record.hba1c !== null && (record.weightKg || record.heightCm)
                            ? ' · '
                            : null}
                          {record.weightKg !== null ? `${record.weightKg} kg` : null}
                          {record.weightKg !== null && record.heightCm !== null ? ' · ' : null}
                          {record.heightCm !== null ? `${record.heightCm} cm` : null}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="schedule-action schedule-action--danger"
                        disabled={busy}
                        onClick={() => handleDelete(record)}
                      >
                        Hapus
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className="next-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
            Kembali
          </button>
        </div>
      </div>

      <NextVisitCard />
    </div>
  );
}
